import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { api } from '~/api/client'
import type { SharePayload } from '~/api/types'

import { Announced } from './Announced'
import { Gig } from './Gig'
import { Room } from './Room'
import { Tonight } from './Tonight'
import { Under } from './Under'
import { Week } from './Week'

/** The address prefix, which `spa.SERVED_PREFIXES` and `PORTED_PREFIXES` both carry. */
export const SHARE_PAGE = '/share-page/'

type State =
  | { status: 'loading' }
  | { status: 'failed'; why: string }
  | { status: 'ready'; payload: SharePayload }

/**
 * The page headless Chrome loads to draw a card: one fetch, one component, nothing to
 * press. Outside `<Shell />`, so there is no masthead over the frame and no footer under
 * it, and the card is the whole document.
 *
 * The card's address is the data's: `/share-page/<card>` fetches `/share-data/<card>` with
 * the same query, so a card the server refuses is a page that never becomes ready and the
 * renderer says why — it has already checked the card exists before it spends a browser.
 */
export function SharePage() {
  const { pathname, search } = useLocation()
  const rest = pathname.startsWith(SHARE_PAGE) ? pathname.slice(SHARE_PAGE.length) : ''
  const [state, setState] = useState<State>({ status: 'loading' })

  useEffect(() => {
    let live = true
    setState({ status: 'loading' })
    api
      .share(rest, search)
      .then(payload => {
        if (live) setState({ status: 'ready', payload })
      })
      .catch((error: unknown) => {
        if (live) setState({ status: 'failed', why: error instanceof Error ? error.message : String(error) })
      })
    return () => {
      live = false
    }
  }, [rest, search])

  if (state.status === 'loading') return null
  if (state.status === 'failed') return <p>no card: {state.why}</p>
  const { payload } = state
  switch (payload.kind) {
    case 'week':
      return <Week {...payload} />
    case 'gig':
      return <Gig {...payload} />
    case 'tonight':
      return <Tonight {...payload} />
    case 'under':
      return <Under {...payload} />
    case 'room':
      return <Room {...payload} />
    case 'announced':
      return <Announced {...payload} />
    default:
      return <p>no card: unknown kind</p>
  }
}
